/**
 * GMA-1617 (CompassCare Web 2/14) — Overview command-center page.
 *
 * Composes the dashboard content: the KPI strip, the active-alerts rail, and the
 * client status grid, plus one shared DetailDrawer. Two things are lifted here
 * so the parts stay in sync:
 *   - `activeFilter` — the KPI tiles AND the grid's filter chips both drive it,
 *     so clicking a tile scopes the grid and vice-versa (single source).
 *   - `detail` — selecting a client (grid) or an alert (rail) opens the shared
 *     drawer, so an item reaches full detail in one click (the ≤2-click AC).
 *
 * Presentational: data (clients / alerts / kpis) is injected by the route so
 * this stays testable. The left Sidebar is app-shell chrome and lives outside
 * this page. Type-specific drawer bodies are a later slice — the drawer here
 * shows a concise summary of the selected item.
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import KpiStrip from "../../components/web/KpiStrip";
import QuickActions from "../../components/web/QuickActions";
import AlertsRail from "../../components/web/AlertsRail";
import ClientGrid from "../../components/web/ClientGrid";
import DetailDrawer from "../../components/web/DetailDrawer";
import SeverityBadge from "../../components/web/SeverityBadge";
import { OFFLINE_STATUS } from "./overviewModel";
import { formatRelativeTime } from "../../utils/webFormat";
import { theme } from "../../styles/designTokens";

const ENV_LABELS = Object.freeze({
  group_home: "Group Home",
  independent: "Independent",
});

function detailTitle(detail) {
  if (!detail) return "";
  if (detail.type === "client") {
    return detail.data?.name || "Client detail";
  }
  const a = detail.data || {};
  return `${a.type || "Alert"} — ${a.clientName || "Unknown client"}`;
}

function DetailBody({ detail }) {
  if (!detail) return null;
  const d = detail.data || {};
  if (detail.type === "client") {
    return (
      <dl style={{ margin: 0, display: "grid", gap: 8 }}>
        <Row label="Status">
          {d.status === OFFLINE_STATUS ? (
            // "offline" is a connectivity state, not a clinical severity —
            // render it muted/distinct instead of via SeverityBadge (which
            // would normalize the unknown level to a green "Normal" badge).
            <span style={{ color: theme.textMuted }}>Offline</span>
          ) : (
            <SeverityBadge level={d.status} />
          )}
        </Row>
        <Row label="Living environment">
          {ENV_LABELS[d.livingEnv] || "—"}
        </Row>
        <Row label="Last update">{formatRelativeTime(d.lastUpdate) || "—"}</Row>
      </dl>
    );
  }
  return (
    <dl style={{ margin: 0, display: "grid", gap: 8 }}>
      <Row label="Severity">
        <SeverityBadge level={d.severity} />
      </Row>
      <Row label="Client">{d.clientName || "Unknown client"}</Row>
      <Row label="Location">{d.location || "—"}</Row>
      <Row label="Raised">{formatRelativeTime(d.createdAt) || "—"}</Row>
    </dl>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <dt style={{ minWidth: 140, color: theme.textMuted, fontSize: 13 }}>
        {label}
      </dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  );
}

export default function OverviewPage({
  clients = [],
  alerts = [],
  kpis = [],
  now,
  onNotifyCaregivers,
  onCreateAlert,
  onDispatchTeam,
  onGenerateSitrep,
}) {
  const [activeFilter, setActiveFilter] = useState("all");
  const [detail, setDetail] = useState(null); // { type: "client"|"alert", data } | null

  const closeDetail = () => setDetail(null);

  return (
    <div
      data-testid="overview-page"
      style={{
        display: "grid",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      <KpiStrip
        kpis={kpis}
        activeFilter={activeFilter}
        onSelectFilter={setActiveFilter}
      />
      <QuickActions
        onNotifyCaregivers={onNotifyCaregivers}
        onCreateAlert={onCreateAlert}
        onDispatchTeam={onDispatchTeam}
        onGenerateSitrep={onGenerateSitrep}
      />
      <AlertsRail
        alerts={alerts}
        now={now}
        onSelectAlert={(alert) => setDetail({ type: "alert", data: alert })}
      />
      <ClientGrid
        clients={clients}
        activeFilter={activeFilter}
        onSelectFilter={setActiveFilter}
        onSelectClient={(client) => setDetail({ type: "client", data: client })}
      />
      <DetailDrawer
        open={detail !== null}
        onClose={closeDetail}
        title={detailTitle(detail)}
        type={detail?.type}
      >
        <DetailBody detail={detail} />
      </DetailDrawer>
    </div>
  );
}

OverviewPage.propTypes = {
  /** Client roster; { id, name, status, livingEnv, lastUpdate }. */
  clients: PropTypes.array,
  /** Active alerts; { id, severity, type, clientName, location, createdAt }. */
  alerts: PropTypes.array,
  /** KPI tile descriptors; { key, label, value, unit?, severity?, filter? }. */
  kpis: PropTypes.array,
  /** Reference time for elapsed display (defaults inside children to Date.now()). */
  now: PropTypes.number,
  /** Notify the client's caregivers (quick action; disabled until wired). */
  onNotifyCaregivers: PropTypes.func,
  /** Manually create an alert (quick action; disabled until wired). */
  onCreateAlert: PropTypes.func,
  /** Dispatch a response team (quick action; disabled until wired). */
  onDispatchTeam: PropTypes.func,
  /** Generate a situation report / BLUF (quick action; disabled until wired). */
  onGenerateSitrep: PropTypes.func,
};
